// FreeResponse — unified container for "user types own answer + reveals
// reference + self-assesses" question types.
// Per LLM_NATIVE_ARCHITECTURE.md §10 + 陛下 19AN insight:
//
//   "形式上其实都一样，毕竟人的认知模式没变。" (The form is the same; human
//    cognition hasn't changed.) — short_answer / calculation / translation
//   share the structure (prompt → think → write → reveal reference → self-
//   assess); they differ only in field shape.
//
// Discriminated union over `kind`:
//
//   - `short_answer`  — prose / explanation / brief argumentation
//   - `calculation`   — numeric / expression result with optional units +
//                       tolerance; workedSteps for derivation
//   - `translation`   — sourceText (in source language) → user produces
//                       translation in targetLanguage
//
// Why a unified schema (vs three separate top-level types):
//   - Single view can render all three (one switch on kind)
//   - Single ingest stage produces all three
//   - Single i18n namespace
//   - Same self-assessment + AI-grade flow regardless of kind
//
// Commercialization pivot point (陛下 19AN decision):
//   `aiScore?: AIScoreSchema | null` is reserved INTERFACE. Free tier:
//   user self-assesses against `referenceAnswer` + `reasoning`. Premium tier
//   (later): a backend grader fills in `aiScore`. Schema reserves the slot
//   so existing data forward-migrates when grading ships. Per MANIFESTO §1.3a
//   "运行时 AI 增强" + §1.3 v1.1 token-billed upsell channel.
//
// 19BH Day 2 Step 2.4: ItemTags base applied to each variant. The shared
//   `baseFreeResponseFields` block is reused by ShortAnswer / Calculation /
//   Translation; we apply the v3 → v4 coerce preprocess at the variant level
//   (z.preprocess on each schema before passing to discriminatedUnion).
import { z } from 'zod';
import { CognitiveLevelSchema } from './cognitive-level.js';
import { EssayAIScoreSchema } from './essay.js';
import { ChunkRefSchema } from './source-ref.js';
import { ItemTagsSchema, coerceItemTagsV3ToV4 } from './item-tags.js';
import { ConceptOmissionSchema } from './concept-omission.js';
// Session 19AO: SelfAssessmentDimensionSchema moved to ./self-assessment.js for
// reuse across free_response / essay / proof_drill. Re-exported here for
// back-compat; new callers should import from @examforge/shared/schemas barrel.
import { SelfAssessmentDimensionSchema } from './self-assessment.js';
export { SelfAssessmentDimensionSchema };
/** Final-value spec for `kind: 'calculation'` items. Tolerance allows numeric
 *  matches like 3.14 ≈ 3.142; for symbolic / set / tuple answers, type other
 *  than 'numerical' makes tolerance moot. */
export const CalculationFinalValueSchema = z.object({
    type: z.enum(['numerical', 'expression', 'set', 'tuple', 'string'])
        .describe('Shape of the answer. Drives match heuristics.'),
    value: z.string().min(1)
        .describe('Canonical answer as a string (LaTeX expression, decimal, set notation, etc).'),
    units: z.string().optional()
        .describe('Units (e.g. "m/s", "kg", "USD"). Omit when dimensionless.'),
    tolerance: z.number().nonnegative().optional()
        .describe('Numeric tolerance (e.g. 0.01) — only meaningful when type=numerical.'),
}).passthrough();
/**
 * Shared fields across the 3 variants. Applied via `ItemTagsSchema.extend(...)`
 * so each variant inherits the v4 anchor tags (caseIds/topicIds/conceptIds).
 *
 * Note: cannot be a const object passed to .extend() because each variant's
 * `kind` literal needs to be at the same nesting level. We inline the shape
 * in each variant below.
 */
const sharedExtendShape = {
    id: z.string().regex(/^fr[a-z0-9_-]+$/, 'free-response id must start with "fr"')
        .describe('Unique snake_case slug starting with `fr` (e.g. `fr_calc_master_theorem_01`).'),
    /** @deprecated v3 alias of caseIds[0]. */
    caseId: z.string().min(1).nullable().optional()
        .describe('@deprecated v3 alias of caseIds[0]. Use caseIds[] · v4 unifies single/multi-chapter.'),
    /** @deprecated v3 alias of topicIds[0]. */
    topicId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of topicIds[0].'),
    subtopicId: z.string().nullable().optional()
        .describe('Subtopic id.'),
    prompt: z.string().min(20)
        .describe('The question / problem statement. ≥20 chars. Markdown + math allowed.'),
    scaffold: z.array(z.string().min(8)).nullish()
        .describe('Optional ordered prompts to structure the user response (e.g. "1. Define X", "2. Apply Y", "3. Conclude"). Each ≥8 chars. 3-6 items typical.'),
    hints: z.array(z.string().min(10)).max(5).nullish()
        .describe('Optional progressive hints the user can reveal one-by-one when stuck. Each ≥10 chars; should escalate (broad → specific). Max 5 items.'),
    referenceAnswer: z.string().min(80)
        .describe('The reference answer in canonical form. ≥80 chars (real answer · not "see notes"). Markdown + math allowed. Constraint Session 19AO.'),
    reasoning: z.string().min(80)
        .describe('How to arrive at the reference answer — the *thinking process*. ≥80 chars. Distinct from referenceAnswer (the *result*). Helps the user grade their own work even if their final form differs.'),
    selfAssessmentRubric: z.array(SelfAssessmentDimensionSchema).min(1).max(6)
        .describe('1-6 rubric dimensions. User self-scores each on a 0-3 scale at submission time.'),
    difficulty: z.enum(['easy', 'medium', 'hard'])
        .describe('Standard difficulty.'),
    cognitiveLevel: CognitiveLevelSchema.optional()
        .describe('Bloom level.'),
    /**
     * Premium-feature interface RESERVATION (Session 19AN · 陛下 commercial pivot).
     * Always null in free tier. When premium grader is enabled, backend fills
     * this in asynchronously after user committment. View's visibility gate
     * (LLM_NATIVE §2.2) keys on `aiScore !== null`.
     */
    aiScore: EssayAIScoreSchema.nullable().optional()
        .describe('Premium-only AI auto-grade. Free tier always omits / null.'),
    /** @deprecated v3 alias of conceptIds[0]. */
    conceptTag: z.string().min(1).max(80).nullish()
        .describe('@deprecated v3 alias of conceptIds[0]. Use conceptIds[].'),
    sourceRef: ChunkRefSchema.nullish()
        .describe('19BG+2 Phase 5 (DEBT-AW-SOURCEREF): citation anchor. Snippet substring-verified against source. Optional for back-compat.'),
    omittedConcepts: z.array(ConceptOmissionSchema).nullish()
        .describe('K18 soft escape · concepts the LLM declared materially insufficient for this stage.'),
};
/** kind: 'short_answer' — text response. */
export const ShortAnswerSchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    kind: z.literal('short_answer'),
    ...sharedExtendShape,
}).passthrough());
/** kind: 'calculation' — numeric / expression with derivation. */
export const CalculationSchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    kind: z.literal('calculation'),
    ...sharedExtendShape,
    finalValue: CalculationFinalValueSchema
        .describe('The canonical final answer. Shown after user commits.'),
    workedSteps: z.array(z.string().min(1)).nullish()
        .describe('Optional ordered steps that arrive at finalValue. Markdown + LaTeX. If omitted, reasoning serves as derivation prose.'),
}).passthrough());
/** kind: 'translation' — source text → target-language translation. */
export const TranslationSchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    kind: z.literal('translation'),
    ...sharedExtendShape,
    sourceText: z.string().min(1)
        .describe('The text to translate, in the source language (any).'),
    targetLanguage: z.string().min(2).max(8)
        .describe('Target language code (e.g. "zh", "en", "fr"). Drives UI label only; semantics enforced via prompt + rubric.'),
}).passthrough());
/**
 * Discriminated union over kind. Because each variant is wrapped in
 * z.preprocess, the union must use z.union() rather than z.discriminatedUnion
 * (which requires plain ZodObject inputs). The kind literal still drives
 * variant selection, just via z.union's parse-each-and-pick algorithm.
 */
export const FreeResponseSchema = z.union([
    ShortAnswerSchema,
    CalculationSchema,
    TranslationSchema,
]);
/** Convenience: type-guard helpers for narrowing in views. */
export const isShortAnswer = (fr) => fr.kind === 'short_answer';
export const isCalculation = (fr) => fr.kind === 'calculation';
export const isTranslation = (fr) => fr.kind === 'translation';
//# sourceMappingURL=free-response.js.map